import { atom } from 'jotai'
import {
  RevenueClassification,
  RevenueRecognitionMethod
} from 'common/types/revenueRecognition.types'
import { ServicePeriodField } from '../../common/fields'
import { DiscountField } from './fields'
import {
  createFieldAtom,
  createRevRecDependentFieldAtom
} from '../../common/atoms'
import {
  DEFAULT_QUANTITY,
  DEFAULT_RATE_DISPLAY,
  DEFAULT_REVENUE_CLASSIFICATION,
  DEFAULT_REVENUE_RECOGNITION_METHOD
} from '../../constants'
import { IntegrationServices } from '@sequencehq/api'
import { RateDisplay } from 'Invoices/types'

export type SubItem = {
  id: string
  title: string
  quantity: string
  rate: string
}

export type FormState = {
  title: string
  quantity: string
  rate: string
  descriptionEnabled: boolean
  description: string
  subItemsEnabled: boolean
  subItems: SubItem[]
  discountEnabled: boolean
  discount: DiscountField
  rateDisplay: RateDisplay
  servicePeriod: ServicePeriodField
  taxCategoryId: string
  revenueRecognitionMethod: RevenueRecognitionMethod | undefined
  revenueClassification: RevenueClassification | undefined
  externalIds: Array<{ key: IntegrationServices; value: string }>
  productId?: string
}

export const createBlankSubItem = (): SubItem => ({
  id: crypto.randomUUID(),
  title: '',
  quantity: DEFAULT_QUANTITY,
  rate: ''
})

export const INITIAL_STATE: FormState = {
  title: '',
  quantity: DEFAULT_QUANTITY,
  rate: '',
  descriptionEnabled: false,
  description: '',
  subItemsEnabled: false,
  subItems: [createBlankSubItem()],
  discountEnabled: false,
  discount: {
    title: '',
    amount: ''
  },
  rateDisplay: DEFAULT_RATE_DISPLAY,
  servicePeriod: undefined,
  taxCategoryId: '',
  revenueRecognitionMethod: DEFAULT_REVENUE_RECOGNITION_METHOD,
  revenueClassification: DEFAULT_REVENUE_CLASSIFICATION,
  externalIds: [],
  productId: undefined
}

export const formStateAtom = atom<FormState>(INITIAL_STATE)

export const titleAtom = createFieldAtom(formStateAtom, 'title')
export const quantityAtom = atom(
  get => {
    const state = get(formStateAtom)
    return state.subItemsEnabled ? '' : state.quantity
  },
  (get, set, quantity: string) => {
    const state = get(formStateAtom)
    if (state.subItemsEnabled) return
    set(formStateAtom, { ...state, quantity })
  }
)
export const rateAtom = atom(
  get => {
    const state = get(formStateAtom)
    return state.subItemsEnabled ? '' : state.rate
  },
  (get, set, rate: string) => {
    const state = get(formStateAtom)
    if (state.subItemsEnabled) return
    set(formStateAtom, { ...state, rate })
  }
)
export const descriptionEnabledAtom = createFieldAtom(
  formStateAtom,
  'descriptionEnabled'
)
export const descriptionAtom = createFieldAtom(formStateAtom, 'description')
export const subItemsEnabledAtom = createFieldAtom(
  formStateAtom,
  'subItemsEnabled'
)
export const subItemsAtom = createFieldAtom(formStateAtom, 'subItems')
export const discountEnabledAtom = createFieldAtom(
  formStateAtom,
  'discountEnabled'
)
export const discountAtom = createFieldAtom(formStateAtom, 'discount')
export const rateDisplayAtom = createFieldAtom(formStateAtom, 'rateDisplay')
export const taxCategoryIdAtom = createFieldAtom(formStateAtom, 'taxCategoryId')
export const revenueRecognitionMethodAtom = createFieldAtom(
  formStateAtom,
  'revenueRecognitionMethod'
)
export const externalIdsAtom = createFieldAtom(formStateAtom, 'externalIds')
export const productIdAtom = createFieldAtom(formStateAtom, 'productId')

export const servicePeriodAtom = createRevRecDependentFieldAtom(
  formStateAtom,
  'servicePeriod'
)
export const revenueClassificationAtom = createRevRecDependentFieldAtom(
  formStateAtom,
  'revenueClassification'
)
